import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import FloorIcon from '@mui/icons-material/Layers';
import RoomIcon from '@mui/icons-material/MeetingRoom';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    styled,
    Typography,
    useTheme
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    FormWrapper
} from "../../components/customStyles/form";
import MultipleDatePicker from "../../components/input/date/multiple/MultipleDatePicker";
import EditOnly from "../../components/permissions/EditOnly";
import DataTable from "../../components/table/data/DataTable";
import { useLocalColumns } from "../../hooks/useTableColumns";
import IProfile from '../../models/profile';
import { ILocalDisplay } from "../../models/requests/local/localDisplay";
import { ETaskType, ITaskProfile } from "../../models/task";
import localService from "../../services/local/LocalService";
import periodidService from "../../services/periodic/PeriodicService";
import profileService from '../../services/profile/ProfileService';
import { usePostPeriodic } from '../../hooks/periodic/usePeriodicMutations';
import { useTranslatedTaskName } from '../../utils/taskNameTranslation';

export const EntryWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
}));

interface ITaskSchedule {
    taskIds: number[];
    localIds: number[];
}

const PeriodicAddPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const translateTaskName = useTranslatedTaskName();
    const [taskDetails, setTaskDetails] = useState<ITaskProfile[]>([]);
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [scheduledDates, setScheduledDates] = useState<Date[]>([]);
    const [locals, setLocals] = useState<ILocalDisplay[]>([]);
    const [selectedLocals, setSelectedLocals] = useState<ILocalDisplay[]>([]);
    const [taskSchedule, setTaskSchedule] = useState<ITaskSchedule>({
        taskIds: [],
        localIds: [],
    });
    const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);
    const [profiles, setProfiles] = useState<IProfile[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<number>(0);
    const { mutate, isPending } = usePostPeriodic();

    const onCreatePeriodic = async () => {
        if (!taskSchedule.localIds.length || !taskSchedule.taskIds.length || !selectedDates.length) return;

        const formattedDates = selectedDates.map(date => moment.utc(date).toDate());

        mutate({
            taskIds: taskSchedule.taskIds,
            localIds: taskSchedule.localIds,
            dates: formattedDates
        }, {
            onSuccess: () => {
                navigate("..");
            },
            onError: (error) => {
            }
        });
    };

    const columns = useLocalColumns();
    useEffect(() => {
        profileService.GetProfilesByPropertyGroups().then(setProfiles)
    }, [])

    useEffect(() => {
        if (selectedProfile === 0) return;
        localService.GetLocalsByProfileId(selectedProfile).then((localsData) => {
            const localDisplays: ILocalDisplay[] = localsData.map((local) => ({
                id: local.local.id,
                name: local.local.name,
                profileName: local.profile.name,
                floorName: local.floor.name,
                buildingName: local.building.name,
                tagMacAddress: local.local.tagMacAddress || "☓",
                codes: local.codes.map((code) => code),
            })); setLocals(localDisplays);
            setTaskSchedule(prev => ({ ...prev, localIds: [], taskIds: [] }));
            setSelectedLocals([]);
            setTaskDetails([]);
            setSelectedDates([]);
        });
    }, [selectedProfile]);


    const fetchTasksForSelectedLocals = async () => {
        if (selectedLocals.length === 0) return;
        const details = await periodidService.GetTasksWithProfile(selectedLocals.map(f => f.id));
        setTaskDetails(details.filter(detail => detail.task.type !== ETaskType.Weekly));
    };

    const groupedLocals = selectedLocals.reduce((acc, local) => {
        if (!acc[local.buildingName]) acc[local.buildingName] = {};
        if (!acc[local.buildingName][local.floorName]) acc[local.buildingName][local.floorName] = [];
        acc[local.buildingName][local.floorName].push(local);
        return acc;
    }, {} as Record<string, Record<string, ILocalDisplay[]>>);

    const onDeselectLocal = (localId: number) => {
        const newLocalIds = taskSchedule.localIds.filter(id => id !== localId);
        setTaskSchedule(prev => ({ ...prev, localIds: newLocalIds }));
        setSelectedLocals(prev => prev.filter(local => local.id !== localId));
    };

    return (
        <Box >
            <Grid container spacing={3} sx={{
                marginBottom: 1,
                flexWrap: 'nowrap',
                flexDirection: { xs: 'column', md: 'row' },
            }}>
                <Grid item xs={12} md={8} sx={{ height: '100%', width: "100%" }}>
                    <Paper sx={{
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: theme.shadows[3]
                    }}>
                        <FormWrapper>
                            <Typography variant="h6" sx={{ marginBottom: '1em' }}>
                                {t('table.profile.name')}
                            </Typography>
                            <EntryWrapper sx={{ mb: 3 }}>
                                <Select
                                    notched
                                    size="small"
                                    fullWidth
                                    defaultValue={-1}
                                    value={selectedProfile}
                                    onChange={(event) =>
                                        setSelectedProfile(Number(event.target.value))
                                    }
                                    id="profile-select"
                                >
                                    {profiles.map((profile) => (
                                        <MenuItem
                                            key={`profile_${profile.id}`}
                                            value={profile.id}
                                        >
                                            {profile.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </EntryWrapper>
                            <Typography variant="h6" sx={{ marginBottom: '1em' }}>
                                {t('table.local.locals')}
                            </Typography>
                            <DataTable
                                height="400px"
                                dense
                                checkable
                                rows={locals}
                                columns={columns.map(column => {
                                    if (column.field === 'tagMacAddress' || column.field === "routes") return { ...column, hidden: true }
                                    return column;
                                })}
                                isLoading={false}
                                onRowClick={(row) =>
                                    navigate('edit', { state: { ...row } })
                                }
                                getRowNavTo={(row) => 'edit'}
                                onSelectionChange={(selectedIds) => {
                                    const localIds = selectedIds.map(Number);
                                    setTaskSchedule(prev => ({ ...prev, localIds }));
                                    setSelectedLocals(locals.filter(local => localIds.includes(local.id)));
                                }}
                                selectedIds={taskSchedule.localIds}
                            />
                            <EntryWrapper sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ marginTop: '1em' }}>
                                    {t('table.task.tasks')}
                                </Typography>
                            </EntryWrapper>
                            <EntryWrapper sx={{ mb: 3 }}>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    onClose={(_, reason) => {
                                        setAutoCompleteOpen(false);
                                    }}
                                    disableCloseOnSelect
                                    options={[
                                        ...(taskDetails.length > 0 ? [{ id: 'select-all', name: t("header.all") }] : []),
                                        ...[...taskDetails]
                                            .sort((a, b) => translateTaskName(a.task).localeCompare(translateTaskName(b.task)))
                                            .map(detail => ({
                                                ...detail.task,
                                                id: Number(detail.task.id)
                                            }))
                                    ]}
                                    value={taskDetails
                                        .map(detail => detail.task)
                                        .filter(task => taskSchedule.taskIds.includes(task.id))}
                                    getOptionLabel={(option) => option.id === 'select-all' ? option.name : translateTaskName(option)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(_, newTasks) => {
                                        const isSelectAll = newTasks.some(t => t.id === 'select-all');
                                        const allTaskIds = taskDetails.map(detail => detail.task.id);

                                        setTaskSchedule(prev => ({
                                            ...prev,
                                            taskIds: isSelectAll
                                                ? prev.taskIds.length === allTaskIds.length ? [] : allTaskIds
                                                : newTasks.filter(t => t.id !== 'select-all').map(t => Number(t.id))
                                        }));
                                    }}
                                    renderOption={(props, option: any) => (
                                        <li {...props} key={option.id}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                px: 2,
                                                py: 1,
                                                '&:hover': { backgroundColor: theme.palette.action.hover }
                                            }}>
                                                <Checkbox
                                                    checked={option.id === 'select-all'
                                                        ? taskSchedule.taskIds.length === taskDetails.length
                                                        : taskSchedule.taskIds.includes(option.id)
                                                    }
                                                    sx={{ mr: 1 }}
                                                />
                                                <Typography variant="body2" sx={{
                                                    fontWeight: option.id === 'select-all' ? 600 : 400,
                                                    color: option.id === 'select-all'
                                                        ? theme.palette.primary.main
                                                        : theme.palette.text.primary
                                                }}>
                                                    {option.id === 'select-all' ? option.name : translateTaskName(option)}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value
                                            .filter(task => task.id !== 'select-all')
                                            .map((task, index) => (
                                                <Chip
                                                    label={translateTaskName(task)}
                                                    {...getTagProps({ index })}
                                                    sx={{
                                                        m: 0.5,
                                                        backgroundColor: theme.palette.secondary.light,
                                                        color: theme.palette.secondary.contrastText,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: theme.palette.secondary.contrastText
                                                        }
                                                    }}
                                                />
                                            ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder={t("periodic.selectTasks")}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.background.paper
                                                }
                                            }}
                                        />
                                    )}
                                    disabled={selectedLocals.length === 0}
                                    onOpen={() => {
                                        setAutoCompleteOpen(true);
                                        fetchTasksForSelectedLocals();
                                    }}
                                    componentsProps={{
                                        paper: {
                                            sx: {
                                                borderRadius: 2,
                                                boxShadow: theme.shadows[3],
                                                '& .MuiAutocomplete-listbox': {
                                                    py: 0
                                                }
                                            }
                                        }
                                    }}
                                />
                            </EntryWrapper>

                            <EntryWrapper>
                                <MultipleDatePicker
                                    isDisabled={!taskSchedule.localIds.length || !taskSchedule.taskIds.length}
                                    disablePast
                                    maxDate={moment().add(1, "year").endOf("month").toDate()}
                                    selectedDates={selectedDates.filter((date) =>
                                        moment(date).isSameOrAfter(new Date(), "day")
                                    )}
                                    setSelectedDates={setSelectedDates}
                                    scheduledDates={scheduledDates}
                                    setScheduledDates={setScheduledDates}
                                    hasRecurrence
                                />
                            </EntryWrapper>
                        </FormWrapper>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4} >
                    <Paper sx={{
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: theme.shadows[3],
                        backgroundColor: theme.palette.background.paper
                    }}>
                        <Typography variant="h6" sx={{
                            mb: 3,
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            {t('local.selectedLocal')}
                            <Chip
                                label={selectedLocals.length}
                                size="small"
                                sx={{
                                    ml: 1,
                                    backgroundColor: theme.palette.primary.light,
                                    color: theme.palette.primary.contrastText
                                }}
                            />
                        </Typography>

                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            pr: 1,
                            '&::-webkit-scrollbar': { width: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.grey[400],
                                borderRadius: 3
                            }
                        }}>
                            {selectedLocals.length > 0 ? (
                                Object.entries(groupedLocals).map(([buildingName, floors]) => (
                                    <Box key={buildingName} sx={{ mb: 3 }}>
                                        <Box sx={{
                                            p: 1.5,
                                            mb: 2,
                                            backgroundColor: theme.palette.grey[100],
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}>
                                            <BusinessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                            <Typography variant="subtitle1" sx={{
                                                fontWeight: 600,
                                                color: theme.palette.primary.main
                                            }}>
                                                {buildingName}
                                            </Typography>
                                        </Box>

                                        {Object.entries(floors).map(([floorName, locals]) => (
                                            <Box key={floorName} sx={{ ml: 2, mb: 3 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 1.5,
                                                    color: theme.palette.text.secondary
                                                }}>
                                                    <FloorIcon fontSize="small" />
                                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                        {floorName}
                                                    </Typography>
                                                </Box>

                                                {locals.map((local) => (
                                                    <Box
                                                        key={local.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            p: 1.5,
                                                            mb: 1,
                                                            borderRadius: 2,
                                                            transition: '0.2s',
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover,
                                                                transform: 'translateX(4px)'
                                                            }
                                                        }}
                                                        onClick={() => onDeselectLocal(local.id)}
                                                    >
                                                        <Checkbox
                                                            checked
                                                            size="small"
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                '&.Mui-checked': { color: theme.palette.primary.main }
                                                            }}
                                                        />
                                                        <RoomIcon fontSize="small" sx={{
                                                            mr: 1.5,
                                                            color: theme.palette.text.secondary
                                                        }} />
                                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                                            {local.name}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                color: theme.palette.error.main,
                                                                '&:hover': { backgroundColor: 'transparent' }
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))}
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="body2" sx={{
                                        color: theme.palette.text.secondary,
                                        maxWidth: 200
                                    }}>
                                        {t('local.noLocalSelected')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{
                            mt: 3,
                            pt: 2,
                            borderTop: `1px solid ${theme.palette.divider}`
                        }}>
                            <EditOnly>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                color="secondary"
                                disabled={isPending || !taskSchedule.localIds.length || !taskSchedule.taskIds.length || !selectedDates.length}
                                onClick={onCreatePeriodic}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    textTransform: 'none',
                                    boxShadow: theme.shadows[2],
                                    '&:disabled': {
                                        backgroundColor: theme.palette.action.disabledBackground,
                                        color: theme.palette.text.disabled
                                    },
                                    '&:hover': {
                                        boxShadow: theme.shadows[4],
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >

                                {isPending ? t("common:loading") : t("common:button.addPeriodic")}
                            </Button>
                            </EditOnly>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PeriodicAddPage;